import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Paper,
  Chip,
  Grid,
  TextField,
  CircularProgress
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { Alert } from "@material-ui/lab";
import ButtonAtm from "../../atomic/Button/Button";

const MuiDialogTitle = withStyles((theme) => ({
  root: { margin: 0, padding: theme.spacing(2) },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}))((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon className="close-button" />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const MuiDialogContent = withStyles(() => ({
  root: { padding: 16 },
}))(DialogContent);

const useStyles = makeStyles((theme) => ({
  channelPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: "1px solid #e0e0e0",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
      borderColor: "#d71f26",
    },
  },
  channelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: "1px solid #f0f0f0",
  },
  channelLogoWrapper: {
    width: "100%",
    height: 75,
    borderRadius: 12,
    backgroundColor: "#f8f9fa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
    border: "1px solid #eee",
  },
  channelLogoImg: { width: 125, height: 90, objectFit: "contain" },
  channelName: { fontWeight: 600, fontSize: 18, color: "#333" },
  activeChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #a5d6a7",
  },
  inactiveChip: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    border: "1px solid #ffcc80",
  },
  parameterSection: {
    backgroundColor: "#fafafa",
    borderRadius: 8,
    padding: theme.spacing(2),
  },
  parameterField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: "#fafafa",
      "&:hover": { backgroundColor: "#fff" },
      "&.Mui-focused": { backgroundColor: "#fff" },
    },
  },
  saveButton: {
    marginTop: theme.spacing(1),
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    textTransform: "none",
  },
}));

export default function ChannelSetupModal({
  open,
  onClose,
  channels,
  onSave,
  syncFetching,
  syncData,
  syncError,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [channelParams, setChannelParams] = useState({});

  useEffect(() => {
    if (channels && channels.length > 0) {
      const initialParams = {};
      channels.forEach((channel) => {
        initialParams[channel.id] = {};
        if (channel.parameterArray) {
          Object.keys(channel.parameterArray).forEach((paramKey) => {
            const existingValue = channel.channelDetail?.parameterArray?.[paramKey] || "";
            initialParams[channel.id][paramKey] = existingValue;
          });
        }
      });
      setChannelParams(initialParams);
    }
  }, [channels]);

  const handleParamChange = (channelId, paramKey, value) => {
    setChannelParams((prev) => ({
      ...prev,
      [channelId]: { ...(prev[channelId] || {}), [paramKey]: value },
    }));
  };

  const handleSave = (channelId) => {
    const params = { channel_id: channelId, ...(channelParams[channelId] || {}) };
    onSave(params);
  };

  return (
    <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth>
      <MuiDialogTitle className="red-bottom-border" onClose={onClose}>
        {t("enw-reputation_management-channel_setup")}
      </MuiDialogTitle>

      <MuiDialogContent style={{ backgroundColor: "#f5f5f5", padding: 24 }}>
        {syncData?.status === 200 && (
          <Alert severity="success" style={{ marginBottom: 16, borderRadius: 8 }}>
            {t("enw-reputation_management-channel_saved")}
          </Alert>
        )}
        {syncError && (
          <Alert severity="error" style={{ marginBottom: 16, borderRadius: 8 }}>
            {syncError?.response?.data?.message || t("enw-error-message")}
          </Alert>
        )}

        <Alert
          severity="info"
          style={{ marginBottom: 24, borderRadius: 8 }}
          icon={<i className="fas fa-info-circle" />}
        >
          {t("enw-reputation_management-channel_info")}
        </Alert>

        {channels?.map((channel) => (
          <Paper key={channel.id} className={classes.channelPaper} elevation={0}>
            <Box className={classes.channelHeader + " rm-setup-top-grid"}>
              <Box display="flex" alignItems="center" className="rm-setup-grid" style={{ gap: 16 }}>
                <Box className={classes.channelLogoWrapper}>
                  <img
                    src={channel.logoUrl}
                    alt={channel.name}
                    className={classes.channelLogoImg}
                  />
                </Box>
                <Box>
                  <Typography className={classes.channelName}>{channel.name}</Typography>
                </Box>
              </Box>

              <Chip
                icon={
                  channel.channelDetail?.fetchStatus?.status === 1 ? (
                    <i className="fas fa-check-circle" style={{ fontSize: 14, color: "#2e7d32", marginTop: 5 }} />
                  ) : (
                    <i className="fas fa-exclamation-circle" style={{ fontSize: 14, color: "#e65100", marginTop: 5 }} />
                  )
                }
                label={
                  channel.channelDetail?.fetchStatus?.status === 1
                    ? t("enw-input-active")
                    : t("enw-input-inactive")
                }
                size="small"
                className={
                  channel.channelDetail?.fetchStatus?.status === 1 ? classes.activeChip + " rm-setup-active-status-chip" : classes.inactiveChip + " rm-setup-active-status-chip"
                }
              />
            </Box>

            <Box className={classes.parameterSection}>
              <Grid container spacing={2} alignItems="center">
                {channel.parameterArray &&
                  Object.keys(channel.parameterArray).map((paramKey) => (
                    <Grid item xs={12} sm={6} md={8} key={paramKey}>
                      <TextField
                        fullWidth
                        label={channel.parameterArray[paramKey].name}
                        variant="outlined"
                        size="small"
                        className={classes.parameterField}
                        value={channelParams[channel.id]?.[paramKey] || ""}
                        onChange={(e) => handleParamChange(channel.id, paramKey, e.target.value)}
                        placeholder={`${channel.parameterArray[paramKey].name}`}
                        InputProps={{
                          startAdornment: (
                            <i className="fas fa-link" style={{ marginRight: 8, color: "#9e9e9e", fontSize: 14 }} />
                          ),
                        }}
                      />
                    </Grid>
                  ))}

                <Grid item xs={12} sm={6} md={4}>
                  <ButtonAtm
                    fullWidth
                    className={`rm-channel-setup-save-btn room-rate-modal-save bg-red ${classes.saveButton}`}
                    style={{ marginTop: 40, marginLeft: 12 }}
                    buttontext={
                      syncFetching ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <i className="fas fa-save" style={{ marginRight: 8 }} />
                          {t("btn-save")}
                        </Box>
                      )
                    }
                    onClick={() => handleSave(channel.id)}
                    disabled={syncFetching}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        ))}
      </MuiDialogContent>
      <style>{`
          @media (max-width: 767px) {
            .MuiDialog-paperFullWidth {
              width: calc(100% - 24px);
            }
        }
      `}</style>
    </Dialog>
  );
}